/**
 * Pinch to zoom, as arithmetic.
 *
 * A receipt is a photograph of small print, and the one thing somebody opens
 * it to do is read a line of it. So the viewer has to zoom, and on a phone
 * that means two fingers — which is a transform this file computes and a
 * `<div>` renders, with no DOM in here at all so the awkward half can be
 * tested. The awkward half really is awkward: the wrong anchor point makes the
 * image slide away from under the fingers doing the pinching, and the wrong
 * clamp lets somebody fling it off the screen and leave themselves looking at
 * grey.
 *
 * **Zoom is anchored to the point between the fingers, not to the middle.**
 * Scaling about the centre moves whatever was under the fingers somewhere
 * else, so the gesture fights the person making it: they pinch on the total,
 * and the total leaves. The rule that fixes it is one line — the content
 * point under the focus stays under the focus — and everything below is that
 * line solved for the translation.
 *
 * **The transform is `translate` then `scale`, about the element's own
 * centre**, which is what `transform: translate(x, y) scale(k)` means with the
 * default `transform-origin`. The maths has to agree with the CSS or the
 * clamping will be right about a picture that is somewhere else.
 *
 * Why not `touch-action: pinch-zoom` and the browser's own? Because the
 * browser's zooms the *page*, and a full-bleed overlay with a toolbar on it is
 * not a page anybody wants scaled — the buttons grow, the image is clipped by
 * the visual viewport, and the way back out is a gesture on something that has
 * moved. Zooming the image alone keeps the way out where it was put.
 */

/** A point, in the coordinate space of the element being zoomed. */
export type Point = { x: number; y: number }

/** Where the picture is: scaled by `scale`, then shifted by `x`/`y`. */
export type ZoomState = { scale: number; x: number; y: number }

/** Unzoomed, uncentred — a picture sitting exactly where it was laid out. */
export const NO_ZOOM: ZoomState = { scale: 1, x: 0, y: 0 }

/** Below 1 the image would be smaller than the space it has; there is no point. */
export const MIN_SCALE = 1

/**
 * Eight times is past the pixels of a 2000px scan on a phone screen, which is
 * far enough to read a smudged total and not so far that a stray pinch lands
 * somebody inside a single letter with no idea which one.
 */
export const MAX_SCALE = 8

/** What a double tap goes to, and comes back from. Enough to read a line. */
export const DOUBLE_TAP_SCALE = 2.5

export const clampScale = (scale: number): number =>
  Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale))

/**
 * The box the picture is viewed in, and — when they differ — the size of the
 * picture actually drawn inside it.
 *
 * They differ for almost every receipt. A tall thin photograph in a phone's
 * frame is letterboxed: the drawn image is narrower than the frame, with empty
 * space either side. Clamping against the *frame* would allow half a frame of
 * travel on an axis where the picture has no overflow at all, which is a drag
 * that slides the receipt out into the black — the exact failure the clamp
 * exists to prevent. `contentWidth`/`contentHeight` are the rendered size of
 * the image, and default to the frame when a caller has nothing better.
 */
export type Viewport = {
  width: number
  height: number
  contentWidth?: number
  contentHeight?: number
}

/**
 * Puts the picture back inside its box.
 *
 * The rule is that no edge of the *drawn image* may come inside the frame once
 * it is big enough to cover it: the slack on an axis is half the overflow of
 * the scaled content over the frame, and zero when the content does not fill
 * the frame at all. At `scale = 1` that leaves no slack in either direction,
 * which is also what makes letting go of a pinch-out snap the picture back
 * where it started rather than a few pixels adrift.
 *
 * Each axis is clamped on its own. A tall receipt zoomed to fit its width has
 * slack vertically and none horizontally, and clamping them together would
 * either lock the pan or let the picture drift sideways off the screen.
 */
export function clampOffset(state: ZoomState, viewport: Viewport): ZoomState {
  const scale = clampScale(state.scale)
  const contentWidth = viewport.contentWidth ?? viewport.width
  const contentHeight = viewport.contentHeight ?? viewport.height
  const slackX = Math.max(0, (contentWidth * scale - viewport.width) / 2)
  const slackY = Math.max(0, (contentHeight * scale - viewport.height) / 2)

  return {
    scale,
    // `+ 0` so that clamping a negative offset to zero gives `0` rather than
    // `-0`, which would otherwise reach the stylesheet as `translate(0px,
    // -0px)`. Harmless to render and confusing to read in a test failure.
    x: Math.min(slackX, Math.max(-slackX, state.x)) + 0,
    y: Math.min(slackY, Math.max(-slackY, state.y)) + 0,
  }
}

/**
 * Scales about a point, so that whatever was under it stays under it.
 *
 * With the transform `p' = (p - centre) * k + centre + t`, the content point
 * `p` under a focus `f` before the change has to be under `f` after it:
 *
 *     (f - c - t₀) / k₀ = (f - c - t₁) / k₁
 *     t₁ = f - c - (f - c - t₀) * k₁ / k₀
 *
 * which is the two lines below, with `f - c` measured from the centre of the
 * viewport because that is where CSS puts the origin.
 */
export function zoomAbout(
  state: ZoomState,
  focus: Point,
  nextScale: number,
  viewport: Viewport,
): ZoomState {
  const scale = clampScale(nextScale)
  const fromCentreX = focus.x - viewport.width / 2
  const fromCentreY = focus.y - viewport.height / 2
  const ratio = scale / state.scale

  return clampOffset(
    {
      scale,
      x: fromCentreX - (fromCentreX - state.x) * ratio,
      y: fromCentreY - (fromCentreY - state.y) * ratio,
    },
    viewport,
  )
}

/** Two fingers, as the only two things a pinch is: how far apart, and where. */
export type Pinch = { distance: number; centre: Point }

export function pinchOf(a: Point, b: Point): Pinch {
  return {
    distance: Math.hypot(b.x - a.x, b.y - a.y),
    centre: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
  }
}

/**
 * One frame of a two-finger gesture: the fingers spread by some ratio and the
 * point between them moved, so the picture scales by that ratio about that
 * point and travels with it.
 *
 * A `from.distance` of zero — two fingers on the same pixel, which a stray
 * palm produces — would be a division by zero and an infinite scale, so it is
 * treated as no change at all rather than as an instruction.
 */
export function pinchTo(
  state: ZoomState,
  from: Pinch,
  to: Pinch,
  viewport: Viewport,
): ZoomState {
  const ratio = from.distance <= 0 ? 1 : to.distance / from.distance
  const zoomed = zoomAbout(state, to.centre, state.scale * ratio, viewport)

  return clampOffset(
    {
      scale: zoomed.scale,
      x: zoomed.x + (to.centre.x - from.centre.x),
      y: zoomed.y + (to.centre.y - from.centre.y),
    },
    viewport,
  )
}

/** Dragging one finger, once there is something to drag: only when zoomed in. */
export function panBy(state: ZoomState, dx: number, dy: number, viewport: Viewport): ZoomState {
  if (state.scale <= MIN_SCALE) return state
  return clampOffset({ scale: state.scale, x: state.x + dx, y: state.y + dy }, viewport)
}

/**
 * A double tap: in to `DOUBLE_TAP_SCALE` about the tapped point, or all the
 * way back out if it is already zoomed at all.
 *
 * Out is "already zoomed at all" rather than "at exactly the double-tap
 * scale", because after a pinch the scale is never exactly anything, and a
 * double tap that zoomed *further* in from 3.4× would leave somebody with no
 * gesture that gets them out.
 */
export function doubleTap(state: ZoomState, focus: Point, viewport: Viewport): ZoomState {
  if (state.scale > MIN_SCALE) return NO_ZOOM
  return zoomAbout(state, focus, DOUBLE_TAP_SCALE, viewport)
}

/**
 * A wheel notch, for the half of the world holding a mouse. The step is
 * multiplicative so that zooming out undoes zooming in exactly, which an
 * additive one does not.
 */
export function wheelZoom(
  state: ZoomState,
  focus: Point,
  deltaY: number,
  viewport: Viewport,
): ZoomState {
  const step = Math.exp(-deltaY / 400)
  return zoomAbout(state, focus, state.scale * step, viewport)
}

/** The `transform` this state is, as CSS. The one place the two agree. */
export function transformOf(state: ZoomState): string {
  return `translate(${state.x}px, ${state.y}px) scale(${state.scale})`
}
